import { PopoverPlacement } from "@idui/react-popover";
import { MoveUp } from "@mui/icons-material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  FramerTooltip,
  RideStatusIndicatorConst,
  UserUpdaterIndicatorRides,
} from "@ryde/controls";
import {
  useRideEmptyStationsAllowed,
  useSelectItemsTranslate,
} from "@ryde/redux";
import {
  DirectionEnum,
  distanceFormula,
  durationInMinutesCeil,
  editFormDataAF,
  IRideDto,
  onActivateKeyDown,
  RideCrudModeKeyTuple,
  RideStationPanelMode,
  RideStatusEnum,
  shouldShowUserUpdaterIndicator,
  useDrawers,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { TabsBottomInfoItemStyled } from "../../../../right";
import { RideHeaderDirection } from "./ride-header-direction";
import {
  HeaderLine2SkipAreaGroupSupplierIconStyled,
  HeaderLine2Styled,
  RideHeaderStatusStyled,
  RideHeaderSupplierTriggerStyled,
} from "./ride-header.styled";
import {
  buildRideSupplierHeaderTooltipContent,
  resolveRideSupplierSendStatus,
} from "./ride-supplier-header.utils";
import { RideSupplierSendStatusIcon } from "./ride-supplier-send-status-icon";

export const RideHeaderInfosMobile: FC = () => {
  const { t } = useTranslation();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { skipSettingSupplierByAreaGroup } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );

  const { openEditFormDrawer } = useDrawers();
  const { translateLabel } = useSelectItemsTranslate();
  const { hasPermissionAllowSendBelowMinimum } = useHaveCustomPermissions();

  const supplierSendStatus = resolveRideSupplierSendStatus({
    seatsCount: rideDto?.metadata?.seatsCount,
    minimumPassengers: rideDto?.rideTypeMinimumPassengersCountForSent,
    canSendBelowMinimumByPermission: hasPermissionAllowSendBelowMinimum,
  });
  const supplierTooltipContent = useMemo(
    () =>
      rideDto
        ? buildRideSupplierHeaderTooltipContent({
            rideDto,
            t,
            canSendBelowMinimumByPermission: hasPermissionAllowSendBelowMinimum,
          })
        : undefined,
    [rideDto, t, hasPermissionAllowSendBelowMinimum],
  );

  const handleSupplierClick = (): void => {
    const id = rideDto?.supplierId;

    if (id) {
      openEditFormDrawer({
        entity: "supplier",
        id,
      });
    }
  };

  const handleVehicleTypeClick = (): void => {
    const id = rideDto?.vehicleTypeId;

    if (id) {
      openEditFormDrawer({
        entity: "vehicleType",
        id,
      });
    }
  };

  const handleDriverClick = (): void => {
    const id = rideDto?.driverId;

    if (id) {
      openEditFormDrawer({
        entity: "driver",
        id,
      });
    }
  };

  const status = rideDto?.status || RideStatusEnum.New;
  const statusColorProps = RideStatusIndicatorConst[status];

  const hasSupplier = !!rideDto?.supplierId;
  const hasVehicleType = !!rideDto?.vehicleTypeId;
  const resolvedDriverName = rideDto?.driverId
    ? rideDto.driverName || t("general.NO_NAME")
    : rideDto?.metadata?.driverInfo?.name;
  const hasDriver = !!rideDto?.driverId || !!resolvedDriverName;
  const hasCourse = !!rideDto?.courseId;
  const totalDistance = rideDto?.metadata?.totalDistance;
  const totalDuration = rideDto?.metadata?.totalDuration;
  const seatsCount = rideDto?.metadata?.seatsCount;

  const statusText = useMemo(() => {
    const info = RideStatusIndicatorConst[status];
    return info?.$translateKey ? translateLabel(info.$translateKey) || "" : "";
  }, [status, translateLabel]);

  return (
    <HeaderLine2Styled>
      <TabsBottomInfoItemStyled>
        <RideHeaderDirection
          direction={rideDto?.direction || DirectionEnum.PickUp}
        />
      </TabsBottomInfoItemStyled>

      {!!rideDto && shouldShowUserUpdaterIndicator(rideDto) && (
        <TabsBottomInfoItemStyled>
          <UserUpdaterIndicatorRides rideDto={rideDto} />
        </TabsBottomInfoItemStyled>
      )}

      {hasCourse && (
        <TabsBottomInfoItemStyled>
          <div className="item__content truncate-text">
            <SVG_MUI.Course4SVG
              data-e2e="ride-course-svg"
              className="ride-header-course-icon"
            />
            <label data-e2e="ride-course-info" className="course">
              {rideDto.courseId} {rideDto.courseName}
            </label>
          </div>
        </TabsBottomInfoItemStyled>
      )}

      {!!totalDistance && (
        <TabsBottomInfoItemStyled>
          <label data-e2e="ride-total-distance">
            <span>
              {distanceFormula(totalDistance)} {t("general.KM")}
            </span>
          </label>
        </TabsBottomInfoItemStyled>
      )}

      {!hasCourse && !!seatsCount && (
        <TabsBottomInfoItemStyled>
          <label data-e2e="ride-passengers-count" className="info">
            {seatsCount}
          </label>
          <label>{t("passenger.plural")}</label>
        </TabsBottomInfoItemStyled>
      )}

      {!!totalDuration && (
        <TabsBottomInfoItemStyled className="teal">
          <label data-e2e="ride-total-duration">
            <span>
              {durationInMinutesCeil(totalDuration)} {t("general.MIN")}
            </span>
          </label>
        </TabsBottomInfoItemStyled>
      )}

      <TabsBottomInfoItemStyled className="teal">
        <RideHeaderStatusStyled {...statusColorProps}>
          {statusText}
        </RideHeaderStatusStyled>
      </TabsBottomInfoItemStyled>

      {hasSupplier && (
        <TabsBottomInfoItemStyled className="ride-header-line2-supplier">
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={supplierTooltipContent}
          >
            <RideHeaderSupplierTriggerStyled>
              <div className="item__content truncate-text">
                <label className="label">{t("supplier.single")}</label>
                <span
                  data-e2e="ride-supplier"
                  className="info pointer"
                  role="button"
                  tabIndex={0}
                  onClick={handleSupplierClick}
                  onKeyDown={onActivateKeyDown(handleSupplierClick)}
                >
                  {rideDto.supplierName || t("general.NO_NAME")}
                </span>
              </div>
              <RideSupplierSendStatusIcon status={supplierSendStatus} />
            </RideHeaderSupplierTriggerStyled>
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {hasSupplier && skipSettingSupplierByAreaGroup && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={t("rideType.formLabel.skipSettingSupplierByAreaGroup")}
          >
            <HeaderLine2SkipAreaGroupSupplierIconStyled>
              <MoveUp fontSize="inherit" />
            </HeaderLine2SkipAreaGroupSupplierIconStyled>
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {hasVehicleType && (
        <TabsBottomInfoItemStyled className="ride-header-line2-vehicle-type">
          <div className="item__content truncate-text">
            <label className="label">{t("vehicleType.single")}</label>
            <span
              data-e2e="ride-vehicle-type"
              className="info pointer"
              role="button"
              tabIndex={0}
              onClick={handleVehicleTypeClick}
              onKeyDown={onActivateKeyDown(handleVehicleTypeClick)}
            >
              {rideDto.vehicleTypeName || t("general.NO_NAME")}
            </span>
          </div>
        </TabsBottomInfoItemStyled>
      )}

      {hasDriver && (
        <TabsBottomInfoItemStyled className="ride-header-line2-driver">
          <div className="item__content truncate-text">
            <label className="label">{t("driver.single")}</label>
            <span
              data-e2e="ride-driver"
              className={clsx({
                info: true,
                pointer: !!rideDto?.driverId,
              })}
              role="button"
              tabIndex={0}
              onClick={handleDriverClick}
              onKeyDown={onActivateKeyDown(handleDriverClick)}
            >
              {resolvedDriverName}
            </span>
          </div>
        </TabsBottomInfoItemStyled>
      )}
    </HeaderLine2Styled>
  );
};
